/**
 * One-shot, per-browser touch-hint coordinator.
 *
 * Returns `{ show, dismiss }` for a hint surface (e.g., the boundary drawer's
 * "Tap to add a vertex…" overlay). The hint shows only when (a) the device
 * reports a coarse pointer (touch screen) and (b) the user hasn't already
 * dismissed it on this browser. Dismissal persists in localStorage under the
 * caller-provided key so subsequent renders of the same hint stay quiet.
 *
 * Server-rendered / non-browser environments stay silent — the effect only
 * runs when `window` is defined, avoiding hydration surprises.
 */
import { useCallback, useEffect, useState } from 'react';

export interface TouchHintState {
  show: boolean;
  dismiss: () => void;
}

export function useTouchHint(localStorageKey: string): TouchHintState {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    if (localStorage.getItem(localStorageKey)) return;
    if (typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia('(pointer: coarse)').matches) return;
    setShow(true);
  }, [localStorageKey]);

  const dismiss = useCallback(() => {
    if (typeof localStorage !== 'undefined') localStorage.setItem(localStorageKey, '1');
    setShow(false);
  }, [localStorageKey]);

  return { show, dismiss };
}
